import * as React from 'react'
import { observer } from 'mobx-react'
import JobViewModel from '../JobViewModel'
import { Form, FormGroup, FormRow, TextInputContainer } from 'components/inputs'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
  ModalSheetSection,
  ModalSheetTitle,
  ModalSheetTitleEmphasis,
} from 'components/ModalSheet'
import ErrorBlock from 'components/ErrorBlock'
import ContentLoading from 'components/ContentLoading'
import Button from 'components/Button'
import styles from './CorrectionsDialog.sass'
import QuestionListViewModel from 'jobs/questions/QuestionListViewModel'
import {
  getQuestionComponentRegistryEntry,
  QuestionViewModel,
} from 'jobs/questions/component-registry'
import QuestionEditorCompactItem from 'jobs/components/QuestionEditorCompactItem'

interface CorrectionsDialogProps {
  vm: JobViewModel
}

export default observer(function CorrectionsDialog({
  vm,
}: CorrectionsDialogProps) {
  const isLoadingInitial =
    vm.correctionsVM.fetchQuestions.pending &&
    vm.correctionsVM.questions.questions.length === 0
  const close = vm.correctionsVM.tasks.pending
    ? undefined
    : vm.correctionsVM.close
  const onEnterDown = React.useCallback(
    () => () => vm.correctionsVM.save(),
    [vm],
  )
  return (
    <ModalSheet
      showing={vm.correctionsVM.showing}
      disableCloseOnEsc
      disableCloseOnOutsideClick
      onRequestClose={close}
    >
      <ModalSheetHeader bordered withCloseButton>
        <ModalSheetTitle>
          Make Corrections for{' '}
          <ModalSheetTitleEmphasis>{vm.job.name}</ModalSheetTitleEmphasis>
        </ModalSheetTitle>
      </ModalSheetHeader>
      <ModalSheetContent>
        <Form data-test="corrections-form">
          <ModalSheetSection spaced>
            <FormRow>
              <TextInputContainer
                model={vm.correctionsVM}
                field="reason"
                label="Reason"
                disabled={vm.correctionsVM.tasks.pending}
                placeholder="e.g. the answers were wrong"
                help="Specify why you are making this change."
                data-test="reason-text-input"
              />
            </FormRow>
            <div className={styles.separator} />
            <FormRow>
              <FormGroup>
                {isLoadingInitial ? (
                  <ContentLoading />
                ) : (
                  <QuestionsEditor
                    disabled={vm.correctionsVM.save.pending}
                    vm={vm.correctionsVM.questions}
                    onEnterDown={onEnterDown}
                  />
                )}
              </FormGroup>
            </FormRow>
          </ModalSheetSection>
        </Form>
      </ModalSheetContent>
      {(vm.correctionsVM.save.result === false ||
        vm.correctionsVM.save.rejected) && (
        <ErrorBlock
          error={
            vm.correctionsVM.save.error ||
            'Please check your input and try again.'
          }
          onCloseClick={vm.correctionsVM.save.reset}
          tackOn="bottom"
        />
      )}
      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button
            disabled={vm.correctionsVM.tasks.pending}
            onClick={vm.correctionsVM.close}
            type="default"
            data-test="corrections-cancel-button"
          >
            Cancel
          </Button>
          <Button
            onClick={() => vm.correctionsVM.save()}
            loading={vm.correctionsVM.tasks.pending}
            type="primary"
            title={
              vm.correctionsVM.hasChanged()
                ? vm.correctionsVM.done
                  ? ''
                  : 'You need to answer all questions before you can save.'
                : 'You need to make changes before you can save.'
            }
            disabled={
              vm.correctionsVM.tasks.pending ||
              !vm.correctionsVM.canSaveResponses
            }
            data-test="corrections-save-button"
          >
            Save
          </Button>
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
})

const QuestionsEditor = observer(function QuestionsEditor({
  vm,
  disabled,
  onEnterDown,
}: {
  vm: QuestionListViewModel
  disabled: boolean
  onEnterDown: React.KeyboardEventHandler
}) {
  return (
    <>
      {vm.questions.map((q) => (
        <QuestionItem
          key={q.id}
          question={q}
          disabled={disabled}
          onEnterDown={onEnterDown}
        />
      ))}
    </>
  )
})

const QuestionItem = observer(function QuestionItem({
  question,
  disabled,
  onEnterDown,
}: {
  question: QuestionViewModel
  disabled: boolean
  onEnterDown: React.KeyboardEventHandler
}) {
  const { EditorInputComponent } = getQuestionComponentRegistryEntry(
    question.type,
  )
  return (
    <QuestionEditorCompactItem question={question}>
      <EditorInputComponent
        vm={question}
        disabled={disabled}
        onEnterDown={onEnterDown}
      />
    </QuestionEditorCompactItem>
  )
})
